import React from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import StoreOnboardingChecklistGroup from '../../features/storeDetails/onboarding/components/StoreOnboardingChecklistGroup';
import StoreOnboardingEmailActions from '../../features/storeDetails/onboarding/components/StoreOnboardingEmailActions';
import StoreOnboardingLetter from '../../features/storeDetails/onboarding/components/StoreOnboardingLetter';
import {
    getOnboardingDailyOperationsItems,
    getOnboardingOperationalReadinessItems,
    getOnboardingPosIntegrationItems,
    getOnboardingStateLotteryIntegrationItem,
} from '../../features/storeDetails/onboarding/utils/storeDetailsOnboardingContent';
import type { OnboardingStatusPanelProps } from '../../types/features/storeDetails/onboarding';
import { adminColors, adminTypography } from '../../styles/adminDesignTokens';
import { adminButtonSx, adminButtonToneSx, adminCombineSx, adminSurfaceSx } from '../../styles/adminComponentStyles';

const OnboardingStatusPanel: React.FC<OnboardingStatusPanelProps> = ({
    storeName,
    subscriptionStatus,
    storeOwner,
    storeManager,
    onboardingLoading,
    onboardingError,
    onboardingData,
    onboardingSavingField,
    onboardingEmailRecipientStoreUserId,
    onboardingEmailContacts,
    onboardingEmailSending,
    onboardingEmailResult,
    onSelectEmailRecipient,
    onSendOnboardingEmail,
    onRetryLoadOnboarding,
    onUpdateOnboardingField,
}) => {
    const isSavingOnboardingField = onboardingSavingField !== null;

    return (
        <Paper sx={{ mb: 2, p: 0, borderRadius: 2, border: 'none', boxShadow: 'none' }}>
            <StoreOnboardingEmailActions
                onboardingEmailRecipientStoreUserId={onboardingEmailRecipientStoreUserId}
                onboardingEmailContacts={onboardingEmailContacts}
                onboardingEmailSending={onboardingEmailSending}
                onboardingEmailResult={onboardingEmailResult}
                onSelectEmailRecipient={onSelectEmailRecipient}
                onSendOnboardingEmail={onSendOnboardingEmail}
            />
            {onboardingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : onboardingError ? (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ ...adminTypography.body, color: adminColors.danger }}>{onboardingError}</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onRetryLoadOnboarding}
                        disabled={onboardingLoading}
                        sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
                    >
                        Retry
                    </Button>
                </Stack>
            ) : onboardingData ? (
                <Box sx={adminCombineSx(adminSurfaceSx, { borderRadius: '8px', overflow: 'hidden', p: 2 })}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: '65% 35%' },
                            gap: 2,
                            alignItems: 'start',
                        }}
                    >
                        <StoreOnboardingLetter
                            storeName={storeName}
                            subscriptionStatus={subscriptionStatus}
                            storeOwner={storeOwner}
                            storeManager={storeManager}
                        />

                        <Box sx={{ borderLeft: { lg: '1px solid #ECEEF3' }, pl: { lg: '16px' }, pr: { lg: '16px' } }}>
                            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text, mb: 0.75 }}>Onboarding Steps</Typography>
                            <Box sx={{ background: 'rgb(248, 249, 251)', padding: '20px', borderRadius: '12px' }}>
                            <Typography sx={{ ...adminTypography.bodyStrong, color: '#2C6CA0', mb: 1 }}>One Time Setup</Typography>
                            <StoreOnboardingChecklistGroup
                                title="Lotto + Scratcher POS Operational Readiness"
                                items={getOnboardingOperationalReadinessItems(onboardingData)}
                                isSavingOnboardingField={isSavingOnboardingField}
                                onUpdateOnboardingField={onUpdateOnboardingField}
                            />
                            <StoreOnboardingChecklistGroup
                                title="POS Integration"
                                items={getOnboardingPosIntegrationItems(onboardingData)}
                                isSavingOnboardingField={isSavingOnboardingField}
                                onUpdateOnboardingField={onUpdateOnboardingField}
                            />
                            <StoreOnboardingChecklistGroup
                                title="State Lottery Integration"
                                items={[getOnboardingStateLotteryIntegrationItem(onboardingData)]}
                                isSavingOnboardingField={isSavingOnboardingField}
                                onUpdateOnboardingField={onUpdateOnboardingField}
                            />
                            <StoreOnboardingChecklistGroup
                                title="Audit Scan Setup"
                                items={[
                                    {
                                        label: 'Audit scan method selected and team trained',
                                        value: onboardingData.setup.audit_scan_method_selected_and_team_trained,
                                        field: 'audit_scan_method_selected_and_team_trained',
                                        editable: true,
                                        help: 'Choose the scanner method your store will use and make sure staff are trained before go-live.',
                                    },
                                ]}
                                isSavingOnboardingField={isSavingOnboardingField}
                                onUpdateOnboardingField={onUpdateOnboardingField}
                            />
                            <Box sx={{ borderTop: `1px solid ${adminColors.divider}`, mt: 1.5, pt: 1.5 }}>
                                <Typography sx={{ ...adminTypography.bodyStrong, color: '#2C6CA0', mb: 1 }}>Daily Operations</Typography>
                                <StoreOnboardingChecklistGroup
                                    title=""
                                    items={getOnboardingDailyOperationsItems(onboardingData)}
                                    isSavingOnboardingField={isSavingOnboardingField}
                                    onUpdateOnboardingField={onUpdateOnboardingField}
                                />
                            </Box>
                           
                            </Box>
                             <Typography sx={{ ...adminTypography.helper, display: 'block', color: adminColors.subtleText, mt: 1.5 }}>
                                Last updated: {onboardingData.updated_at ? dayjs(onboardingData.updated_at).format('MM/DD/YYYY h:mm A') : '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </Paper>
    );
};

export default OnboardingStatusPanel;
